import type { MessageChannel } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import type {
  IntegrationWithChannelDataResponse,
  ShopperAiIntegrationType,
} from "@redotech/effect-api-shared/models/shopper-ai/integration/integration";
import {
  SupportTicketFilterNames,
  supportTicketFilterData,
} from "@redotech/effect-api-shared/schema/support-ticket-filters";
import ChannelsIcon from "@redotech/redo-icons/message-chat-circle.svg";
import { ArrayStringFilterOperator } from "@redotech/redo-model/views/advanced-filters/array-string-filter";
import { AdvancedFilterType } from "@redotech/redo-model/views/advanced-filters/generic-advanced-filter-data";
import type { ArrayStringTableFilter } from "@redotech/redo-web/advanced-filters/advanced-filter";
import type { TextRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { MessageChannelIcons } from "../ui/thread-channel-ui";

/** All channels that can appear in a Support v2 ticket. */
const ALL_FILTERABLE_CHANNELS: MessageChannel[] = [
  "gmail",
  "outlook",
  "instagramDm",
  "instagramComments",
  "facebookDm",
  "facebookComments",
  "tiktok",
  "attentive",
  "sms",
  "mobileMessage",
  "postscript",
  "redoChat",
  "voice",
  "contactForm",
];

/**
 * Human-readable labels for filterable channels.
 */
const FilterableChannelReadable: Record<MessageChannel, string> = {
  gmail: "Gmail",
  outlook: "Outlook",
  instagramDm: "Instagram DM",
  instagramComments: "Instagram Comments",
  facebookDm: "Facebook DM",
  facebookComments: "Facebook Comments",
  tiktok: "TikTok",
  attentive: "Attentive",
  sms: "SMS",
  mobileMessage: "Mobile Message",
  postscript: "Postscript",
  redoChat: "Redo Chat",
  voice: "Voice",
  contactForm: "Contact Form",
  internalNote: "Internal Note",
};

/**
 * Maps an integration type to all MessageChannels it can produce.
 * Instagram and Facebook each produce two channels (DM and comments).
 */
const integrationTypeToChannels: Record<
  ShopperAiIntegrationType,
  MessageChannel[]
> = {
  gmail: ["gmail"],
  outlook: ["outlook"],
  instagram: ["instagramDm", "instagramComments"],
  facebook: ["facebookDm", "facebookComments"],
  tiktok: ["tiktok"],
  attentive: ["attentive"],
  telnyx_sms: ["sms"],
  sms_phone_number: ["sms"],
  imessage: ["sms"],
  telnyx_voice: ["voice"],
  mobileMessage: ["mobileMessage"],
  postscript: ["postscript"],
  redoChatAi: ["redoChat"],
  redoChatFlow: ["redoChat"],
  redoChatLive: ["redoChat"],
  contactForm: ["contactForm"],
};

/**
 * Returns the channels a merchant has based on their integrations
 * (including deleted/deactivated ones for historical filtering).
 */
function getAvailableChannels(
  integrations: readonly IntegrationWithChannelDataResponse[],
): MessageChannel[] {
  const channelSet = new Set<MessageChannel>();
  for (const integration of integrations) {
    const channels = integrationTypeToChannels[integration.integrationType];
    for (const channel of channels) {
      channelSet.add(channel);
    }
  }
  return ALL_FILTERABLE_CHANNELS.filter((ch) => channelSet.has(ch));
}

/**
 * Channels filter uses MessageChannel values to match what's stored
 * in OpenSearch and returned by the API.
 *
 * When integrations are provided, only channels the merchant has (or had)
 * integrations for are shown. When integrations are undefined (still loading),
 * all channels are shown.
 */
export function createChannelsAdvancedFilter(
  integrations?: readonly IntegrationWithChannelDataResponse[],
): ArrayStringTableFilter<MessageChannel> {
  const channels =
    integrations !== undefined
      ? getAvailableChannels(integrations)
      : ALL_FILTERABLE_CHANNELS;

  return {
    type: AdvancedFilterType.ARRAY_TO_STRING,
    Icon: ChannelsIcon,
    operators: [
      ArrayStringFilterOperator.ANY_OF,
      ArrayStringFilterOperator.NONE_OF,
    ],
    data: supportTicketFilterData[SupportTicketFilterNames.CHANNELS],
    itemLabel: "channel",
    itemLabelPlural: "channels",
    values: channels,
    display: (item: MessageChannel) => FilterableChannelReadable[item] ?? item,
    displayName: "Channels",
    itemGenerator: (
      item: MessageChannel,
    ): TextRedoListItem<MessageChannel> => ({
      id: item,
      value: item,
      type: "text",
      text: FilterableChannelReadable[item] ?? item,
      leadingItem: { type: "icon", Icon: MessageChannelIcons[item] },
    }),
  };
}
